import { createTheme, Theme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface ZIndex {
    inlineEditorPopper: number;
    inlineEditorMenu: number;
  }
}

const defaultTheme = createTheme();
const customPaperShadow =
  '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 1px rgba(0,0,0,0.12)';
const customShadows = defaultTheme.shadows;
customShadows[1] = customPaperShadow;

const zIndex = {
  inlineEditorPopper: 1500,
  inlineEditorMenu: 1600,
};

const materialCheckIconMask =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='black' d='M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'/%3E%3C/svg%3E\")";

/** Checkmark for the selected option in single-select MUI Select menus (MUI Check icon, right-aligned). */
const selectedMenuItemCheckmarkStyle = {
  display: 'flex',
  alignItems: 'center',
  '&::after': {
    content: '""',
    width: 14,
    height: 14,
    flexShrink: 0,
    marginLeft: 'auto',
    backgroundColor: 'currentColor',
    maskImage: materialCheckIconMask,
    WebkitMaskImage: materialCheckIconMask,
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    maskPosition: 'center',
    WebkitMaskPosition: 'center',
    maskSize: 'contain',
    WebkitMaskSize: 'contain',
  },
};

const selectSelectedOptionCheckmarkSx = {
  '& .MuiMenuItem-root.Mui-selected:not(:has(.MuiCheckbox-root))': selectedMenuItemCheckmarkStyle,
};

export const selectMenuCheckmarkSx = selectSelectedOptionCheckmarkSx;

const components = {
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontSize: '0.8125rem',
        padding: '3px 8px',
        lineHeight: '1.4',
      },
      head: {
        fontSize: '0.75rem',
        fontWeight: 600,
        padding: '4px 8px',
      },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: {
        height: '30px',
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: '32px',
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        minHeight: '32px',
        padding: '4px 12px',
        fontSize: '0.8125rem',
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: '0.8125rem',
        padding: '4px 12px',
        minHeight: '30px',
        '&.Mui-selected:not(:has(.MuiCheckbox-root))': selectedMenuItemCheckmarkStyle,
      },
    },
  },

  MuiListItem: {
    styleOverrides: {
      root: {
        paddingTop: '2px',
        paddingBottom: '2px',
      },
    },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: {
        paddingTop: '4px',
        paddingBottom: '4px',
        fontSize: '0.8125rem',
      },
    },
  },

  MuiListItemText: {
    styleOverrides: {
      primary: {
        fontSize: '0.8125rem',
      },
      secondary: {
        fontSize: '0.75rem',
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        height: '20px',
        fontSize: '0.75rem',
      },
      label: {
        padding: '0 6px',
      },
    },
  },

  MuiSvgIcon: {
    styleOverrides: {
      root: {
        fontSize: '1rem',
      },
      fontSizeSmall: {
        fontSize: '0.875rem',
      },
      fontSizeLarge: {
        fontSize: '1.25rem',
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        padding: '4px',
      },
      sizeSmall: {
        padding: '2px',
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        minHeight: '28px',
        lineHeight: '1.4rem',
        padding: '3px 10px',
        fontSize: '0.8125rem',
      },
      sizeSmall: {
        minHeight: '24px',
        padding: '2px 8px',
        fontSize: '0.75rem',
      },
    },
  },

  MuiToolbar: {
    styleOverrides: {
      root: {
        minHeight: '40px !important',
        padding: '0 12px !important',
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: '0.75rem',
        padding: '4px 8px',
      },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      // Match MuiSelect size=small input inset (7.5px / 14px).
      input: {
        paddingTop: '7.5px !important',
        paddingBottom: '7.5px !important',
        paddingLeft: '14px !important',
        paddingRight: '4px !important',
      },
      listbox: {
        padding: '4px 0',
        '& .MuiAutocomplete-option': {
          fontSize: '0.8125rem',
          padding: '4px 10px',
          minHeight: '30px',
        },
      },
      groupLabel: {
        fontSize: '0.75rem',
        lineHeight: '28px',
      },
      noOptions: {
        fontSize: '0.8125rem',
        padding: '6px 10px',
      },
      root: {
        '& .MuiOutlinedInput-root': {
          paddingTop: '0 !important',
          paddingBottom: '0 !important',
          paddingLeft: '0 !important',
          '& fieldset': {
            borderColor: 'divider',
          },
          '&:hover fieldset': {
            borderColor: 'text.secondary',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px background.paper inset',
            WebkitTextFillColor: 'text.primary',
            borderRadius: '4px',
          },
          '& input:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 100px background.paper inset',
            WebkitTextFillColor: 'text.primary',
          },
          '& input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px background.paper inset',
            WebkitTextFillColor: 'text.primary',
          },
        },
      },
    },
  },

  MuiSelect: {
    defaultProps: {
      size: 'small' as const,
      MenuProps: {
        sx: selectSelectedOptionCheckmarkSx,
      },
    },
    styleOverrides: {
      select: {
        '&.MuiInputBase-inputSizeSmall': {
          paddingTop: '7.5px',
          paddingBottom: '7.5px',
        },
      },
      root: {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'divider',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'text.secondary',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
        },
      },
    },
  },

  MuiOutlinedInput: {
    defaultProps: {
      size: 'small' as const,
    },
    styleOverrides: {
      root: {
        '& fieldset': {
          borderColor: 'divider',
        },
        '&:hover fieldset': {
          borderColor: 'text.secondary',
        },
        '&.Mui-focused fieldset': {
          borderColor: 'primary.main',
        },
        '& input:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 100px background.paper inset',
          WebkitTextFillColor: 'text.primary',
          borderRadius: '4px',
        },
        '& input:-webkit-autofill:hover': {
          WebkitBoxShadow: '0 0 0 100px background.paper inset',
          WebkitTextFillColor: 'text.primary',
        },
        '& input:-webkit-autofill:focus': {
          WebkitBoxShadow: '0 0 0 100px background.paper inset',
          WebkitTextFillColor: 'text.primary',
        },
      },
    },
  },

  MuiFilledInput: {
    styleOverrides: {
      root: {
        backgroundColor: 'action.hover',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        '&.Mui-focused': {
          backgroundColor: 'action.hover',
        },
        '& input:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 100px action.hover inset',
          WebkitTextFillColor: 'text.primary',
          borderRadius: '4px',
        },
        '& input:-webkit-autofill:hover': {
          WebkitBoxShadow: '0 0 0 100px action.hover inset',
          WebkitTextFillColor: 'text.primary',
        },
        '& input:-webkit-autofill:focus': {
          WebkitBoxShadow: '0 0 0 100px action.hover inset',
          WebkitTextFillColor: 'text.primary',
        },
      },
    },
  },

  MuiInput: {
    styleOverrides: {
      root: {
        '&:before': {
          borderBottomColor: 'divider',
        },
        '&:hover:not(.Mui-disabled):before': {
          borderBottomColor: 'text.secondary',
        },
        '&.Mui-focused:after': {
          borderBottomColor: 'primary.main',
        },
        '& input:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 100px background.paper inset',
          WebkitTextFillColor: 'text.primary',
        },
        '& input:-webkit-autofill:hover': {
          WebkitBoxShadow: '0 0 0 100px background.paper inset',
          WebkitTextFillColor: 'text.primary',
        },
        '& input:-webkit-autofill:focus': {
          WebkitBoxShadow: '0 0 0 100px background.paper inset',
          WebkitTextFillColor: 'text.primary',
        },
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: 'text.secondary',
        '&.Mui-focused': {
          color: 'primary.main',
        },
      },
    },
  },

  MuiFormControl: {
    styleOverrides: {
      root: {
        '& .MuiInputLabel-root': {
          color: 'text.secondary',
          '&.Mui-focused': {
            color: 'primary.main',
          },
        },
      },
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root: {
        padding: '3px',
        color: 'text.secondary',
        '&.Mui-checked': {
          color: 'primary.main',
        },
        '&.MuiCheckbox-indeterminate': {
          color: 'primary.main',
        },
      },
    },
  },

  MuiRadio: {
    styleOverrides: {
      root: {
        padding: '3px',
        color: 'text.secondary',
        '&.Mui-checked': {
          color: 'primary.main',
        },
      },
    },
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: {
        fontSize: '0.6875rem',
        marginTop: '2px',
        lineHeight: 1.3,
      },
    },
  },

  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        marginLeft: '-4px',
        gap: '2px',
        '& .MuiFormControlLabel-label': {
          color: 'text.primary',
          fontSize: '0.8125rem',
        },
      },
    },
  },

  MuiInputBase: {
    styleOverrides: {
      input: {
        fontSize: '0.8125rem',
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: '4px',
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '4px',
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '10px 12px',
        '&:last-child': {
          paddingBottom: '10px',
        },
      },
    },
  },

  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: '8px 12px',
      },
      title: {
        fontSize: '0.875rem',
        fontWeight: 600,
      },
      subheader: {
        fontSize: '0.75rem',
      },
    },
  },

  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: '6px 12px',
      },
    },
  },

  MuiAccordion: {
    styleOverrides: {
      root: {
        '&:before': {
          display: 'none',
        },
      },
    },
  },

  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        minHeight: '36px',
        padding: '0 12px',
        '&.Mui-expanded': {
          minHeight: '36px',
        },
      },
      content: {
        margin: '6px 0',
        '&.Mui-expanded': {
          margin: '6px 0',
        },
      },
    },
  },

  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        padding: '6px 12px 10px',
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: '10px 16px',
        fontSize: '0.9375rem',
        fontWeight: 600,
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '8px 16px',
      },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '8px 16px',
        gap: '6px',
      },
    },
  },

  MuiPopover: {
    styleOverrides: {
      paper: {
        borderRadius: '4px',
      },
    },
  },

  MuiSwitch: {
    styleOverrides: {
      root: {
        '& .MuiSwitch-switchBase': {
          color: 'text.secondary',
          '&.Mui-checked': {
            color: 'primary.main',
            '& + .MuiSwitch-track': {
              backgroundColor: 'primary.main',
            },
          },
        },
        '& .MuiSwitch-track': {
          backgroundColor: 'divider',
        },
      },
    },
  },

  MuiTextField: {
    defaultProps: {
      InputLabelProps: { shrink: true },
      size: 'small' as const,
    },
    styleOverrides: {
      root: {
        '& .MuiInputBase-inputSizeSmall': {
          paddingTop: '7.5px',
          paddingBottom: '7.5px',
        },
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: 'divider',
          },
          '&:hover fieldset': {
            borderColor: 'text.secondary',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px background.paper inset',
            WebkitTextFillColor: 'text.primary',
            borderRadius: '4px',
          },
          '& input:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 100px background.paper inset',
            WebkitTextFillColor: 'text.primary',
          },
          '& input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px background.paper inset',
            WebkitTextFillColor: 'text.primary',
          },
        },
        '& .MuiInputLabel-root': {
          color: 'text.secondary',
          '&.Mui-focused': {
            color: 'primary.main',
          },
        },
      },
    },
  },

  MuiPickersSectionList: {
    styleOverrides: {
      root: {
        padding: '8px 0px !important',
      },
    },
  },

  MuiPagination: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        '& .Mui-selected': {
          color: 'rgb(255, 255, 255) !important',
          backgroundColor: `${theme.palette.primary.main} !important`,
        },
      }),
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        alignItems: 'flex-start',
        padding: '12px 16px',
      },
      icon: {
        padding: 0,
        marginRight: 8,
        // Optically center the 22px icon against the first text line (13px body).
        marginTop: 1,
      },
      message: {
        padding: 0,
      },
    },
  },

  MuiLink: {
    styleOverrides: {
      root: {
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },
};

const themeDark = createTheme({
  shadows: customShadows,
  zIndex,
  components: {
    ...components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: '#484f58 #161b22',
          '&::-webkit-scrollbar-track': {
            background: '#161b22',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#484f58',
          },
          '& *::-webkit-scrollbar-track': {
            background: '#161b22',
          },
          '& *::-webkit-scrollbar-thumb': {
            backgroundColor: '#484f58',
          },
          '& *': {
            scrollbarColor: '#484f58 #161b22',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#58a6ff',
          textDecoration: 'none',
          '&:hover': {
            color: '#79c0ff',
            textDecoration: 'underline',
          },
          '&:visited': {
            color: '#58a6ff',
          },
        },
      },
    },
    MuiTextField: {
      ...components.MuiTextField,
      styleOverrides: {
        ...components.MuiTextField.styleOverrides,
        root: {
          ...components.MuiTextField.styleOverrides.root,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(61, 71, 81, 0.5)',
            },
            '&:hover fieldset': {
              borderColor: '#8b949e',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
            '& input[type="time"]': {
              colorScheme: 'dark',
            },
            '& input:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 100px #161b22 inset',
              WebkitTextFillColor: '#c9d1d9',
              borderRadius: '4px',
            },
            '& input:-webkit-autofill:hover': {
              WebkitBoxShadow: '0 0 0 100px #161b22 inset',
              WebkitTextFillColor: '#c9d1d9',
            },
            '& input:-webkit-autofill:focus': {
              WebkitBoxShadow: '0 0 0 100px #161b22 inset',
              WebkitTextFillColor: '#c9d1d9',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#8b949e',
            '&.Mui-focused': {
              color: 'primary.main',
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      ...components.MuiAutocomplete,
      styleOverrides: {
        ...components.MuiAutocomplete.styleOverrides,
        root: {
          ...components.MuiAutocomplete.styleOverrides.root,
          '& .MuiOutlinedInput-root': {
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
            paddingLeft: '0 !important',
            '& fieldset': {
              borderColor: 'rgba(61, 71, 81, 0.5)',
            },
            '&:hover fieldset': {
              borderColor: '#8b949e',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
            '& input:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 100px #161b22 inset',
              WebkitTextFillColor: '#c9d1d9',
              borderRadius: '4px',
            },
            '& input:-webkit-autofill:hover': {
              WebkitBoxShadow: '0 0 0 100px #161b22 inset',
              WebkitTextFillColor: '#c9d1d9',
            },
            '& input:-webkit-autofill:focus': {
              WebkitBoxShadow: '0 0 0 100px #161b22 inset',
              WebkitTextFillColor: '#c9d1d9',
            },
          },
        },
      },
    },
    MuiSelect: {
      ...components.MuiSelect,
      styleOverrides: {
        ...components.MuiSelect.styleOverrides,
        root: {
          ...components.MuiSelect.styleOverrides.root,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(61, 71, 81, 0.5)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#8b949e',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
          },
        },
      },
    },
    MuiOutlinedInput: {
      ...components.MuiOutlinedInput,
      styleOverrides: {
        ...components.MuiOutlinedInput.styleOverrides,
        root: {
          ...components.MuiOutlinedInput.styleOverrides.root,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(61, 71, 81, 0.5)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#8b949e',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px #161b22 inset',
            WebkitTextFillColor: '#c9d1d9',
            borderRadius: '4px',
          },
          '& input:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 100px #161b22 inset',
            WebkitTextFillColor: '#c9d1d9',
          },
          '& input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px #161b22 inset',
            WebkitTextFillColor: '#c9d1d9',
          },
        },
      },
    },
    MuiFilledInput: {
      ...components.MuiFilledInput,
      styleOverrides: {
        ...components.MuiFilledInput.styleOverrides,
        root: {
          ...components.MuiFilledInput.styleOverrides.root,
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
          },
          '&.Mui-focused': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px rgba(255, 255, 255, 0.04) inset',
            WebkitTextFillColor: '#c9d1d9',
            borderRadius: '4px',
          },
          '& input:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 100px rgba(255, 255, 255, 0.04) inset',
            WebkitTextFillColor: '#c9d1d9',
          },
          '& input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px rgba(255, 255, 255, 0.04) inset',
            WebkitTextFillColor: '#c9d1d9',
          },
        },
      },
    },
    MuiInput: {
      ...components.MuiInput,
      styleOverrides: {
        ...components.MuiInput.styleOverrides,
        root: {
          ...components.MuiInput.styleOverrides.root,
          '&:before': {
            borderBottomColor: 'rgba(61, 71, 81, 0.5)',
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottomColor: '#8b949e',
          },
          '&.Mui-focused:after': {
            borderBottomColor: 'primary.main',
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px #161b22 inset',
            WebkitTextFillColor: '#c9d1d9',
          },
          '& input:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 100px #161b22 inset',
            WebkitTextFillColor: '#c9d1d9',
          },
          '& input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px #161b22 inset',
            WebkitTextFillColor: '#c9d1d9',
          },
        },
      },
    },
    MuiInputLabel: {
      ...components.MuiInputLabel,
      styleOverrides: {
        ...components.MuiInputLabel.styleOverrides,
        root: {
          ...components.MuiInputLabel.styleOverrides.root,
          color: '#8b949e',
          '&.Mui-focused': {
            color: 'primary.main',
          },
        },
      },
    },
    MuiFormControl: {
      ...components.MuiFormControl,
      styleOverrides: {
        ...components.MuiFormControl.styleOverrides,
        root: {
          ...components.MuiFormControl.styleOverrides.root,
          '& .MuiInputLabel-root': {
            color: '#8b949e',
            '&.Mui-focused': {
              color: 'primary.main',
            },
          },
        },
      },
    },
    MuiFormControlLabel: {
      ...components.MuiFormControlLabel,
      styleOverrides: {
        ...components.MuiFormControlLabel.styleOverrides,
        root: {
          ...components.MuiFormControlLabel.styleOverrides.root,
          '& .MuiFormControlLabel-label': {
            color: '#c9d1d9',
          },
        },
      },
    },
    MuiCheckbox: {
      ...components.MuiCheckbox,
      styleOverrides: {
        ...components.MuiCheckbox.styleOverrides,
        root: {
          ...components.MuiCheckbox.styleOverrides.root,
          color: '#8b949e',
          '&.Mui-checked': {
            color: '#58a6ff',
          },
          '&.MuiCheckbox-indeterminate': {
            color: '#58a6ff',
          },
        },
      },
    },
    MuiRadio: {
      ...components.MuiRadio,
      styleOverrides: {
        ...components.MuiRadio.styleOverrides,
        root: {
          ...components.MuiRadio.styleOverrides.root,
          color: '#8b949e',
          '&.Mui-checked': {
            color: 'primary.main',
          },
        },
      },
    },
    MuiSwitch: {
      ...components.MuiSwitch,
      styleOverrides: {
        ...components.MuiSwitch.styleOverrides,
        root: {
          ...components.MuiSwitch.styleOverrides.root,
          '& .MuiSwitch-switchBase': {
            color: '#8b949e',
            '&.Mui-checked': {
              color: 'primary.main',
              '& + .MuiSwitch-track': {
                backgroundColor: 'primary.main',
              },
            },
          },
          '& .MuiSwitch-track': {
            backgroundColor: 'rgba(61, 71, 81, 0.3)',
          },
        },
      },
    },
  },
  palette: {
    primary: { main: '#1e3a8a' },
    secondary: { main: '#0b0d0e' },
    mode: 'dark',
    background: {
      default: '#0d1117',
      paper: '#161b22',
    },
    text: {
      primary: '#c9d1d9',
      secondary: '#8b949e',
    },
    action: {
      selected: 'rgba(255, 255, 255, 0.14)',
      hover: 'rgba(255, 255, 255, 0.04)',
    },
    divider: 'rgba(61, 71, 81, 0.3)',
  },
  typography: {
    fontFamily: ['Inter, sans-serif'].join(','),
    fontSize: 13,
    button: {
      textTransform: 'none',
    },
    body1: {
      fontSize: 13,
    }
  },
});

const themeLight = createTheme({
  shadows: customShadows,
  zIndex,
  components: {
    ...components,
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#0969da',
          textDecoration: 'none',
          '&:hover': {
            color: '#0550ae',
            textDecoration: 'underline',
          },
          '&:visited': {
            color: '#0969da',
          },
        },
      },
    },
    MuiTextField: {
      ...components.MuiTextField,
      styleOverrides: {
        ...components.MuiTextField.styleOverrides,
        root: {
          ...components.MuiTextField.styleOverrides.root,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#d0d7de',
            },
            '&:hover fieldset': {
              borderColor: '#666',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
            '& input[type="time"]': {
              colorScheme: 'light',
            },
            '& input:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 100px #ffffff inset',
              WebkitTextFillColor: '#222',
              borderRadius: '4px',
            },
            '& input:-webkit-autofill:hover': {
              WebkitBoxShadow: '0 0 0 100px #ffffff inset',
              WebkitTextFillColor: '#222',
            },
            '& input:-webkit-autofill:focus': {
              WebkitBoxShadow: '0 0 0 100px #ffffff inset',
              WebkitTextFillColor: '#222',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#666',
            '&.Mui-focused': {
              color: 'primary.main',
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      ...components.MuiAutocomplete,
      styleOverrides: {
        ...components.MuiAutocomplete.styleOverrides,
        root: {
          ...components.MuiAutocomplete.styleOverrides.root,
          '& .MuiOutlinedInput-root': {
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
            paddingLeft: '0 !important',
            '& fieldset': {
              borderColor: '#d0d7de',
            },
            '&:hover fieldset': {
              borderColor: '#666',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
            '& input:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 100px #ffffff inset',
              WebkitTextFillColor: '#222',
              borderRadius: '4px',
            },
            '& input:-webkit-autofill:hover': {
              WebkitBoxShadow: '0 0 0 100px #ffffff inset',
              WebkitTextFillColor: '#222',
            },
            '& input:-webkit-autofill:focus': {
              WebkitBoxShadow: '0 0 0 100px #ffffff inset',
              WebkitTextFillColor: '#222',
            },
          },
        },
      },
    },
    MuiSelect: {
      ...components.MuiSelect,
      styleOverrides: {
        ...components.MuiSelect.styleOverrides,
        root: {
          ...components.MuiSelect.styleOverrides.root,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d0d7de',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#666',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
          },
        },
      },
    },
    MuiOutlinedInput: {
      ...components.MuiOutlinedInput,
      styleOverrides: {
        ...components.MuiOutlinedInput.styleOverrides,
        root: {
          ...components.MuiOutlinedInput.styleOverrides.root,
          '& fieldset': {
            borderColor: '#d0d7de',
          },
          '&:hover fieldset': {
            borderColor: '#666',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px #ffffff inset',
            WebkitTextFillColor: '#222',
            borderRadius: '4px',
          },
          '& input:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 100px #ffffff inset',
            WebkitTextFillColor: '#222',
          },
          '& input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px #ffffff inset',
            WebkitTextFillColor: '#222',
          },
        },
      },
    },
    MuiFilledInput: {
      ...components.MuiFilledInput,
      styleOverrides: {
        ...components.MuiFilledInput.styleOverrides,
        root: {
          ...components.MuiFilledInput.styleOverrides.root,
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-focused': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px rgba(0, 0, 0, 0.04) inset',
            WebkitTextFillColor: '#222',
            borderRadius: '4px',
          },
          '& input:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 100px rgba(0, 0, 0, 0.04) inset',
            WebkitTextFillColor: '#222',
          },
          '& input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px rgba(0, 0, 0, 0.04) inset',
            WebkitTextFillColor: '#222',
          },
        },
      },
    },
    MuiInput: {
      ...components.MuiInput,
      styleOverrides: {
        ...components.MuiInput.styleOverrides,
        root: {
          ...components.MuiInput.styleOverrides.root,
          '&:before': {
            borderBottomColor: '#d0d7de',
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottomColor: '#666',
          },
          '&.Mui-focused:after': {
            borderBottomColor: 'primary.main',
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px #ffffff inset',
            WebkitTextFillColor: '#222',
          },
          '& input:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 100px #ffffff inset',
            WebkitTextFillColor: '#222',
          },
          '& input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px #ffffff inset',
            WebkitTextFillColor: '#222',
          },
        },
      },
    },
    MuiInputLabel: {
      ...components.MuiInputLabel,
      styleOverrides: {
        ...components.MuiInputLabel.styleOverrides,
        root: {
          ...components.MuiInputLabel.styleOverrides.root,
          color: '#666',
          '&.Mui-focused': {
            color: 'primary.main',
          },
        },
      },
    },
    MuiFormControl: {
      ...components.MuiFormControl,
      styleOverrides: {
        ...components.MuiFormControl.styleOverrides,
        root: {
          ...components.MuiFormControl.styleOverrides.root,
          '& .MuiInputLabel-root': {
            color: '#666',
            '&.Mui-focused': {
              color: 'primary.main',
            },
          },
        },
      },
    },
    MuiFormControlLabel: {
      ...components.MuiFormControlLabel,
      styleOverrides: {
        ...components.MuiFormControlLabel.styleOverrides,
        root: {
          ...components.MuiFormControlLabel.styleOverrides.root,
          '& .MuiFormControlLabel-label': {
            color: '#222',
          },
        },
      },
    },
    MuiCheckbox: {
      ...components.MuiCheckbox,
      styleOverrides: {
        ...components.MuiCheckbox.styleOverrides,
        root: {
          ...components.MuiCheckbox.styleOverrides.root,
          color: '#666',
          '&.Mui-checked': {
            color: 'primary.main',
          },
          '&.MuiCheckbox-indeterminate': {
            color: 'primary.main',
          },
        },
      },
    },
    MuiRadio: {
      ...components.MuiRadio,
      styleOverrides: {
        ...components.MuiRadio.styleOverrides,
        root: {
          ...components.MuiRadio.styleOverrides.root,
          color: '#666',
          '&.Mui-checked': {
            color: 'primary.main',
          },
        },
      },
    },
    MuiSwitch: {
      ...components.MuiSwitch,
      styleOverrides: {
        ...components.MuiSwitch.styleOverrides,
        root: {
          ...components.MuiSwitch.styleOverrides.root,
          '& .MuiSwitch-switchBase': {
            color: '#666',
            '&.Mui-checked': {
              color: 'primary.main',
              '& + .MuiSwitch-track': {
                backgroundColor: 'primary.main',
              },
            },
          },
          '& .MuiSwitch-track': {
            backgroundColor: '#e0e0e0',
          },
        },
      },
    },
  },
  palette: {
    primary: { main: '#1e3a8a' },
    secondary: { main: '#0b0d0e' },
    mode: 'light',
    background: {
      default: '#f6f9fc',
      paper: '#ffffff',
    },
    text: {
      primary: '#222',
      secondary: '#666',
    },
    action: {
      selected: 'rgba(0, 0, 0, 0.08)',
      hover: 'rgba(0, 0, 0, 0.04)',
    },
    divider: '#e0e0e0',
  },
  typography: {
    fontFamily: ['Inter, sans-serif'].join(','),
    button: {
      textTransform: 'none',
    },
    fontSize: 13,
    body1: {
      fontSize: 13,
    }
  },
});

export { themeDark, themeLight };
